<script setup lang="ts">
import { useProductStore } from '~/store/product/productStore';
import Prices from '~/components/product/Prices.vue';
import { GLOBAL_SIMILAR_PRODUCT_BY_GENDER, GLOBAL_WISHLIST, PAYPAL_OFFICIAL_WIDGET } from '~/plugins/featureFlag';
import ReviewStars from '~/components/reviews/review-stars.vue';
import VariantSelector from '~/components/product/VariantSelector.vue';
import VoucherBrand from '~/components/product/VoucherBrand.vue';
import { MODAL_CART, MODAL_OPEN, MODAL_PRODUCT_AVAILABILITY, useModalStore } from '~/store/modalStore';
import { useVariantSelectorStore } from '~/store/variantSelectorStore';
import { isMobile } from '~/helpers/utils';
import Loader from '~/components/loaders/Loader.vue';
import Eclat from '~/components/eclats/eclat.vue'
import {money} from "~/helpers/product/productHelper";

const locale = useI18n().locale.value;
const productStore = useProductStore();
const variantSelectorStore = useVariantSelectorStore();
const modalStore = useModalStore();

const {
  product,
  offer,
  hasReviews,
  hasQuestions,
  similarProductByGender,
  productModelCollection,
  variantCollection,
  wasOffer,
  unavailableProductSizes,
  getLowestPriceForSize,
  isLogged
} = storeToRefs(productStore);

const {
  currentVariant
} = storeToRefs(variantSelectorStore);

const emit = defineEmits<{
  (event: 'clickReviews'): void;
}>();

const cartButtonLoading = ref(false);
const wishlistButtonLoading = ref(false);
const paypalButton = ref();

const isPromo = computed(() => {
  const crossedOutPrice = offer.value.price.originalPrice;
  const price = offer.value.price.finalPrice;

  return crossedOutPrice && crossedOutPrice !== 0 && crossedOutPrice > price ? 1 : 0;
})

const scrollToReviews = () => {
  emit('clickReviews');
}

const addToCartButtonScroll = () => {
  const addToCartButton = document.getElementById('add-to-cart');

  if (!addToCartButton) {
    return;
  }

  const placeholder = document.getElementById('add-to-cart-placeholder');
  const footer = document.querySelector('.cfooter');

  if (!placeholder || !footer) {
    return;
  }

  if ((footer as HTMLElement).offsetTop < (window.scrollY + window.innerHeight)) {
      (placeholder as HTMLElement).style.height = (0).toString();
      addToCartButton.classList.remove('product-page__add-basket--fixed');
  } else if (placeholder && placeholder.getBoundingClientRect().top <= 0) {
      if (addToCartButton.offsetHeight > 0) {
        (placeholder as HTMLElement).style.height = `${addToCartButton.offsetHeight}px`;
      }
      addToCartButton.classList.add('product-page__add-basket--fixed');
  } else {
      (placeholder as HTMLElement).style.height = (0).toString();
      addToCartButton.classList.remove('product-page__add-basket--fixed');
  }
};

const addToCart = (merchantProductId: number, productPath: string, price: number, quantity = 1) => {
  if (!currentVariant.value) {
    if (isMobile()) {
      modalStore.displayOverlay = true;
    }
    variantSelectorStore.open();
    return;
  }

  cartButtonLoading.value = true;
  const event = document.createEvent('CustomEvent');
  event.initCustomEvent('addProductToCart', true, true, {
    productId: merchantProductId, quantity, productPath, price
  });

  document.dispatchEvent(event);
}

const updateWishlist = (id: number) => {
  if (!isLogged.value) {
    return;
  }

  if (currentVariant.value === null) {
    variantSelectorStore.open();
    return;
  }

  wishlistButtonLoading.value = true;

  const event = document.createEvent('CustomEvent');
  event.initCustomEvent('addToWishlistEventNuxt', true, true, {
    itemID: id
  });
  document.dispatchEvent(event);
}

const displayProductAvailabilityModal = () => modalStore.displayModal(MODAL_PRODUCT_AVAILABILITY, MODAL_OPEN)

onMounted(() => {
  document.addEventListener('scroll', () => {
    if (window.innerWidth < 769) {
      addToCartButtonScroll()
    }
  })

  document.addEventListener('cartUpdated', () => {
    cartButtonLoading.value = false;
    modalStore.displayModal(MODAL_CART, MODAL_OPEN);
  })

  document.addEventListener('addedToWishlist', () => {
    productStore.updateMerchantProductVariant(product.value.id, offer.value.canonicalMerchantProduct.id).then(() => {
      wishlistButtonLoading.value = false;
    })
  })
})
</script>

<template>
  <div class="product-page__right pr">
    <div>
      <a
          data-tclz-selector="main-brand"
          :data-tclz-label="product.brand.url"
          data-tclz-category="product"
          :href="product.brand.url"
          class="size--title-4 weight--interregular__section-title hover-underline product-page-title">{{ product.brand.name }}</a>
    </div>
    <h1 class="size--title-4 weight--intermedium product-page-subtitle mb-h">{{ product.nameWithoutBrand }}</h1>
    <div v-if="offer.merchantProductLabels.length > 0" class="mt-h mb-h product-page-eclat pr">
      <eclat :label="offer.merchantProductLabels[0]" :absolute="false"/>
    </div>
    <div class="df aifs jcsb fww">
      <prices
              :price-dto="offer.price"
              :is-small="false"
              :was-offer="wasOffer"/>
      <client-only>
        <div
            ref="paypalButton"
            class="w100"
            style="margin-bottom: 5px"
            data-pp-message
            data-pp-style-layout="text"
            data-pp-style-logo-type="alternative"
            data-pp-style-text-color="black"
            data-pp-style-text-align="left"
            :data-pp-buyercountry="locale.toUpperCase()"
            data-pp-style-text-size="14"
            :data-pp-amount="(offer.price.finalPrice / 100).toFixed(2)"/>
      </client-only>
    </div>
    <div v-if="hasReviews || hasQuestions" class="df aic creviews mb-h">
      <review-stars v-if="product.productReviewDTO.globalRateCount" :rate="product.productReviewDTO.globalRate" />
      <span
            v-if="hasReviews"
            class="size--headline weight--intersbold ml-h pointer hover-underline"
            data-tclz-selector="product_reviews"
            :data-tclz-label="product.categoryPath"
            data-tclz-category="product"
            @click="scrollToReviews">
          {{ $t('www.product.reviews.reviews_v2', { 'count': product.productReviewDTO.globalRateCount.toString() }) }}
      </span>
      <span v-if="hasQuestions" :class="`size--headline weight--intersbold ${hasReviews ? 'ml-h' : ''} pointer`">
        <span v-if="hasReviews"> | </span>
        <span
              class="hover-underline"
              data-tclz-selector="product_questions"
              :data-tclz-label="product.categoryPath"
              data-tclz-category="product"
              onclick="scrollToContent('#questions');">
              {{ $t('www.product.questions.amount', {
                'count': product.productQuestionCollection.length.toString()
              }) }}
        </span>
      </span>
    </div>

    <voucher-brand v-if="product.brandVouchers.length > 0" class="mb-1" />
    <div v-if="($isFeatureEnabled as any)(GLOBAL_SIMILAR_PRODUCT_BY_GENDER) && similarProductByGender">
      <div class="df fdc aifs">
        <div class="mb-1 cbtn--gender-select-container">
          <NuxtLink
             :to="similarProductByGender.manID == product.id ? 'javascript:void(0)' : decodeURIComponent(similarProductByGender.manUrl)"
             :class="`cbtn--gender-select ${similarProductByGender.manID == product.id ? ' active' : '' }`"
             data-tclz-selector="switch-gender"
             :data-tclz-label="$t('www.user.form.man')"
             data-tclz-category="product">
            {{ $t('www.user.form.man') }}
          </NuxtLink>
          <NuxtLink
             :to="similarProductByGender.womanID == product.id ? 'javascript:void(0)' : decodeURIComponent(similarProductByGender.womanUrl)"
             :class="`cbtn--gender-select ${similarProductByGender.womanID == product.id ? ' active' : '' }`"
             data-tclz-selector="switch-gender"
             :data-tclz-label="$t('www.user.form.woman')"
             data-tclz-category="product">
            {{ $t('www.user.form.woman') }}
          </NuxtLink>
        </div>
      </div>
    </div>
    <div
        v-if="productModelCollection.length > 0"
        class="mb-1">
      <product-models
                     :canonical-product-id="product.id"
                     :product-model-collection="productModelCollection" />
    </div>
    <product-size-guide
                        v-if="variantCollection.length >= 1"
                        :canonical-product-id="product.id"
                        :category-path="product.categoryPath"
                        :is-sent-to-usizy="product.isSentToUsizy"
                        :locale="locale"
                        :size-guide="product.sizeGuide" />

    <div class=" mb-1">
      <variant-selector v-if="offer.variant.displayName !== ''"/>
      <div
          v-if="getLowestPriceForSize && getLowestPriceForSize.canonicalColorProductID !== offer.canonicalColorID && wasOffer"
          class="product-page__lowest-price-model">
        <NuxtLink
            :to="getLowestPriceForSize.link">{{ $t('www.product.lowest-price-model', {'finalPrice':money(getLowestPriceForSize.price)}) }}</NuxtLink>
      </div>
      <div
          v-if="variantCollection.length <= 1"
          class="w100 mb-1">
        <div
            v-if="unavailableProductSizes.length > 0"
            class="pointer"
            data-tclz-selector="availability-modal"
            :data-tclz-label="product.categoryPath"
            data-tclz-category="product"
            data-tclz-action="email alert click"
            @click="displayProductAvailabilityModal">
          <span class="clink clink--primary ">{{ $t('www.product_availability.label') }}</span>
        </div>
      </div>
    </div>
    <div class="df fdc aic product-page-buttons">
      <div id="add-to-cart-placeholder"/>
      <div class="product-page__add-basket mb-1">
        <div v-if="variantCollection.length >= 1" class="center-text cbtn--add-to-cart">
          <div class="cbtn-container">
            <button
                id="add-to-cart"
                data-tclz-selector="add-to-cart"
                :data-tclz-label="isPromo"
                data-tclz-category="product"
                class="cbtn cbtn--large cbtn--dark"
                @click="() => addToCart(offer.merchantProduct.id, product.categoryPath, offer.price.finalPrice)"
              >
              <Loader v-if="cartButtonLoading" />
              <span v-else>{{ $t('www.product.add_to_basket') }}</span>
            </button>

            <a
                v-if="($isFeatureEnabled as any)(GLOBAL_WISHLIST)"
                id="add-to-wishlist"
                :href="isLogged ? 'javascript:' : $customLocalePath('www_authenticate', {redirect: null})"
                data-tclz-selector="add-to-wishlist"
                :data-tclz-label="isPromo"
                data-tclz-category="product"
                type="button"
                :class="`cbtn--wishlist df jcc aic ${offer.isWishlist && wasOffer ? 'cbtn--wishlist--active' : ''}`"
                @click="() => updateWishlist(offer.merchantProduct.id)">
              <Loader
                  v-if="wishlistButtonLoading"
                  color="primary"/>
              <svg
                  v-else
                  class="cicon cicon--24 cicon--s-dark pe-n">
                <use xlink:href="#wishlist"/>
              </svg>
            </a>
          </div>
        </div>
        <div v-else>
          <div>
            <div class="mb-1">
              {{ $t('www.product.not_available') }}
            </div>
            <a href="">
              <button
                  type="submit"
                  data-tclz-selector="see-similar-products"
                  :data-tclz-label="product.categoryPath"
                  data-tclz-category="product"
                  data-tclz-action="'click on see similar products'"
                  class="cbtn cbtn--medium cbtn--dark">
                {{ $t('www.product.see_other') }}
              </button>
            </a>
          </div>
        </div>
      </div>
      <div
           v-if="variantCollection.length > 0"
           class="w100 mb-h p-1 border-grey-light"
           data-tclz-selector="delivery-details"
           :data-tclz-label="product.categoryPath"
           data-tclz-category="product">
        <product-info-bullet-small />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.product-page-eclat {
  .cproduct__label {
    position: static;
  }
}

.cbtn--wishlist {
  .cloader {
    left: -7px !important;
  }
}
</style>
